/**
 * 첨부 유효기간(보관 기간) 판정 — 단일 출처.
 *
 * 소비처가 셋이다(채팅 말풍선 `ChatWindow.renderAttachments` · 서랍 `AttachmentDrawer` ·
 * 라이트박스 `ImageLightbox`). 판정을 각자 하면 반드시 갈라지므로 여기서만 한다.
 * `channelSearch.ts`의 `matchesChannelQuery()`와 같은 계약.
 *
 * 만료 시각은 서버가 attachments 행에 절대 시각(`expires_at`)으로 박아 내려준다.
 * NULL이면 무기한 보관(기능 도입 이전에 올라간 첨부가 전부 여기 해당한다 — 소급 없음).
 *
 * ⚠ 한계(의도적): 판정이 렌더 시점의 시각 비교라, 채팅방을 열어 둔 채 만료 시각을 넘기면
 * 다음 리렌더까지 화면이 그대로다. 첨부 하나당 타이머를 두는 비용이 이득보다 크다.
 * 최종 실체는 서버에 파일이 남아 있는지 여부이고, 만료된 파일은 스윕이 지워 404가 된다.
 */

/** 만료 임박으로 볼 잔여 일수 */
export const EXPIRY_SOON_DAYS = 7;

export const EXPIRED_FILE_TEXT = '유효기간이 지나 다운로드할 수 없습니다';
export const EXPIRED_IMAGE_TEXT = '유효기간이 지난 사진입니다';
/** 썸네일 격자처럼 폭이 100px 안팎인 칸용. 전체 문구는 title 속성으로 남긴다 */
export const EXPIRED_IMAGE_SHORT_TEXT = '유효기간 만료';
export const EXPIRED_DOWNLOAD_TOAST =
  '유효기간이 지나 다운로드할 수 없습니다. 보낸 사람에게 다시 요청해 주세요.';

// Intl 포맷터는 생성 비용이 커서 모듈 레벨 싱글턴 (date.ts와 같은 이유)
const EXPIRY_DATE_FORMATTER = new Intl.DateTimeFormat('ko-KR', {
  month: 'long',
  day: 'numeric',
});

const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

const parse = (expiresAt?: string | null): Date | null => {
  if (!expiresAt) return null;
  const d = new Date(expiresAt);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** 유효기간이 지났는가. `expiresAt`이 없으면(무기한) 항상 false */
export const isAttachmentExpired = (expiresAt?: string | null): boolean => {
  const d = parse(expiresAt);
  return !!d && d.getTime() <= Date.now();
};

/** 만료까지 남은 일수(달력 기준). 무기한이면 null, 이미 지났으면 음수 */
export const daysUntilExpiry = (expiresAt?: string | null): number | null => {
  const d = parse(expiresAt);
  if (!d) return null;
  return Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);
};

/** 만료가 코앞인가 (7일 이내). 이미 만료됐으면 false — 그때는 만료 문구가 대신 나간다 */
export const isExpiringSoon = (expiresAt?: string | null): boolean => {
  if (isAttachmentExpired(expiresAt)) return false;
  const days = daysUntilExpiry(expiresAt);
  return days !== null && days <= EXPIRY_SOON_DAYS;
};

/**
 * 첨부 카드 부제에 붙는 유효기간 라벨.
 * 무기한이거나 이미 만료됐으면 null (만료는 EXPIRED_FILE_TEXT가 자리를 대신한다).
 *
 * '오늘까지' / '내일까지' / 'N일 후 만료' / '11월 24일까지'
 */
export const formatExpiryLabel = (expiresAt?: string | null): string | null => {
  if (isAttachmentExpired(expiresAt)) return null;
  const d = parse(expiresAt);
  if (!d) return null;
  const days = daysUntilExpiry(expiresAt);
  if (days === null) return null;
  if (days <= 0) return '오늘까지';
  if (days === 1) return '내일까지';
  if (days <= EXPIRY_SOON_DAYS) return `${days}일 후 만료`;
  return `${EXPIRY_DATE_FORMATTER.format(d)}까지`;
};

/** 첨부 미리보기 안내: '첨부한 파일은 90일 뒤 자동으로 삭제됩니다'. 무기한이면 null */
export const formatRetentionNotice = (retentionDays: number): string | null =>
  retentionDays > 0 ? `첨부한 파일은 ${retentionDays}일 뒤 자동으로 삭제됩니다` : null;
